import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ShareModal from '@/components/ShareModal';
import VideoEditorModal from '@/components/VideoEditorModal';
import { 
  ArrowLeft, 
  Upload, 
  Sparkles, 
  Plus, 
  Trash2, 
  GripVertical,
  Download,
  Mic,
  Play,
  Pause,
  FileText,
  Save,
  Share2,
  Video,
  Music,
  Check,
  Scissors,
  Copy,
  Clipboard,
  RefreshCw,
  Pencil,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

// Video Voiceover Card Component — Continuous narration: one video → one script → one audio track
const VideoVoiceoverCard = ({ video, index, projectId, voices, onUpdate, onEditVideo, projectVoiceText, projectSteps }) => {
  const fallbackFromSteps = () => {
    if (!projectSteps || projectSteps.length === 0) return '';
    return projectSteps
      .map(s => (s.description || '').replace(/<[^>]*>/g, ' ').trim())
      .filter(Boolean)
      .join('. ');
  };

  const [text, setText] = useState(video.voiceover_text || projectVoiceText || fallbackFromSteps() || '');
  const [voice, setVoice] = useState(video.voiceover_voice || 'alloy');
  const [generating, setGenerating] = useState(false);
  const [genTaskId, setGenTaskId] = useState(null);
  const [genProgress, setGenProgress] = useState('');
  const [taskStatus, setTaskStatus] = useState(null); // 'interrupted' | 'paused' | 'failed' | null
  const [taskError, setTaskError] = useState(null);
  const [recoverableTaskId, setRecoverableTaskId] = useState(null);
  const [textModified, setTextModified] = useState(false);
  const savedTextRef = useRef(video.voiceover_text || '');
  const [audioCacheBust, setAudioCacheBust] = useState(Date.now());

  // Synced preview player state
  const vidPreviewRef = useRef(null);
  const audioPreviewRef = useRef(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(video.duration || 0);

  const audioSrc = video.voiceover_audio_path ? `${API}/uploads/${video.voiceover_audio_path.split('/').pop()}?cb=${audioCacheBust}` : null;
  const videoSrc = video.path ? `${API}/uploads/${encodeURIComponent(video.path.split('/').pop())}?v=${encodeURIComponent(video.path)}&t=${video.duration || 0}` : null;

  // On mount: check if there's a recoverable task for this video
  useEffect(() => {
    const checkRecoverable = async () => {
      try {
        const res = await axios.get(`${API}/videos/${video.id}/latest-voiceover-task`);
        const task = res.data;
        if (task.status === 'paused') {
          setTaskStatus('paused');
          setTaskError(task.error || '');
          setRecoverableTaskId(task.task_id);
        } else if (task.status === 'failed') {
          setTaskStatus('failed');
          setTaskError(task.error || 'Generation failed');
          setRecoverableTaskId(task.task_id);
        } else if (task.status === 'started' || task.status === 'processing' || task.status === 'interrupted') {
          // Active or auto-resuming task — start polling
          setGenTaskId(task.task_id);
          setGenerating(true);
          setGenProgress(task.status === 'interrupted' ? 'Auto-resuming after restart...' : (task.progress || 'Processing...'));
        }
      } catch { /* no task found */ }
    };
    checkRecoverable();
  }, [video.id]);

  // Poll for voiceover task completion
  useEffect(() => {
    if (!genTaskId) return;
    let cancelled = false;
    let delay = 2000;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await axios.get(`${API}/voiceover-tasks/${genTaskId}`);
          const task = res.data;
          if (task.progress) setGenProgress(task.progress);
          if (task.status === 'completed') {
            setGenerating(false);
            setGenTaskId(null);
            setGenProgress('');
            setTaskStatus(null);
            setTaskError(null);
            setRecoverableTaskId(null);
            setTextModified(false);
            setAudioCacheBust(Date.now());
            setPreviewTime(0);
            setPreviewPlaying(false);
            if (task.result?.combined_text) {
              setText(task.result.combined_text);
              savedTextRef.current = task.result.combined_text;
            }
            onUpdate?.();
            toast.success('Voiceover generated successfully');
            return;
          }
          if (task.status === 'paused') {
            setGenerating(false);
            setGenTaskId(null);
            setGenProgress('');
            setTaskStatus('paused');
            setTaskError(task.error || 'Paused — insufficient credits');
            setRecoverableTaskId(genTaskId);
            toast.error('Generation paused — insufficient credits. Top up and resume.', { duration: 8000 });
            return;
          }
          if (task.status === 'failed') {
            setGenerating(false);
            setGenTaskId(null);
            setGenProgress('');
            setTaskStatus('failed');
            setTaskError(task.error || 'Voiceover generation failed');
            setRecoverableTaskId(genTaskId);
            toast.error(task.error || 'Voiceover generation failed');
            return;
          }
          // 'interrupted' = auto-resuming, keep polling
          if (task.status === 'interrupted') {
            setGenProgress('Auto-resuming after restart...');
          }
        } catch (err) { /* ignore polling errors */ }
        await new Promise(r => setTimeout(r, delay));
        delay = Math.min(delay * 1.3, 8000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [genTaskId]);

  const generateVoiceover = async () => {
    setGenerating(true);
    setGenProgress('Starting...');
    setTaskStatus(null);
    setTaskError(null);
    setRecoverableTaskId(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/videos/${video.id}/generate-voiceover`, {
        text, voice, speed: 1.0
      });
      setGenTaskId(res.data.task_id);
    } catch (err) {
      setGenerating(false);
      setGenProgress('');
      const errMsg = err.response?.data?.detail || 'Failed to start voiceover generation';
      setTaskStatus('failed');
      setTaskError(errMsg);
      toast.error(errMsg);
    }
  };

  const resumeOrRetry = async () => {
    if (!recoverableTaskId) { generateVoiceover(); return; }
    setGenerating(true);
    setGenProgress('Resuming...');
    setTaskStatus(null);
    setTaskError(null);
    try {
      const res = await axios.post(`${API}/retry-voiceover/${recoverableTaskId}`);
      setGenTaskId(res.data.task_id);
      setRecoverableTaskId(null);
    } catch (err) {
      setGenerating(false);
      setGenProgress('');
      const errMsg = err.response?.data?.detail || 'Resume failed';
      setTaskStatus('failed');
      setTaskError(errMsg);
      toast.error(errMsg);
    }
  };

  // Synced preview controls
  const togglePreviewPlay = () => {
    const vid = vidPreviewRef.current;
    const aud = audioPreviewRef.current;
    if (previewPlaying) {
      vid?.pause();
      aud?.pause();
      setPreviewPlaying(false);
    } else {
      if (vid) { vid.muted = true; vid.currentTime = previewTime; }
      if (aud) { aud.currentTime = previewTime; }
      vid?.play().catch(() => {});
      aud?.play().catch(() => {});
      setPreviewPlaying(true);
    }
  };

  const handlePreviewSeek = (e) => {
    const t = parseFloat(e.target.value);
    setPreviewTime(t);
    if (vidPreviewRef.current) vidPreviewRef.current.currentTime = t;
    if (audioPreviewRef.current) audioPreviewRef.current.currentTime = t;
  };

  // Sync loop
  useEffect(() => {
    if (!previewPlaying) return;
    const interval = setInterval(() => {
      const aud = audioPreviewRef.current;
      const vid = vidPreviewRef.current;
      if (aud) {
        setPreviewTime(aud.currentTime);
        if (vid && Math.abs(vid.currentTime - aud.currentTime) > 0.3) {
          vid.currentTime = aud.currentTime;
        }
      } else if (vid) {
        setPreviewTime(vid.currentTime);
      }
    }, 250);
    return () => clearInterval(interval);
  }, [previewPlaying]);

  const fmtTime = (s) => {
    if (!s || !isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const estSpeechSec = wordCount > 0 ? Math.round(wordCount / 2.3 * 10) / 10 : 0;
  const videoDur = video.duration || 0;
  const isStale = video.voiceover_stale;
  const isTextModified = textModified && video.voiceover_generated && !isStale;

  const fmtDuration = (s) => {
    if (!s || s <= 0) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.round(s % 60);
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  return (
    <Card className="overflow-hidden" data-testid={`voiceover-card-${index}`}>
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mic className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-slate-700">{video.filename || `Video ${index + 1}`}</h3>
            {video.voiceover_generated && !isStale && !isTextModified && (
              <span className="px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 rounded font-medium" data-testid="voiceover-status-ok">Ready</span>
            )}
            {isTextModified && (
              <span className="px-1.5 py-0.5 text-[10px] bg-violet-50 text-violet-600 border border-violet-200 rounded font-medium" data-testid="voiceover-status-modified">Modified — Regenerate</span>
            )}
            {isStale && (
              <span className="px-1.5 py-0.5 text-[10px] bg-amber-50 text-amber-600 border border-amber-200 rounded font-medium" data-testid="voiceover-status-stale">Stale — Regenerate</span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400 font-mono">
            <span>Video: <b className="text-slate-600">{fmtDuration(videoDur)}</b></span>
            <span>Words: <b className="text-slate-600">{wordCount}</b></span>
            <span>Est. speech: <b className={`${estSpeechSec > videoDur * 1.1 ? 'text-red-500' : estSpeechSec < videoDur * 0.5 ? 'text-amber-500' : 'text-slate-600'}`}>{fmtDuration(estSpeechSec)}</b></span>
          </div>
        </div>

        {/* === PRIORITY: Task recovery panels (show FIRST, before anything else) === */}
        {taskStatus === 'paused' && !generating && (
          <div className="flex items-center gap-3 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg" data-testid="voiceover-paused-panel">
            <Pause className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">Paused — insufficient credits</p>
              <p className="text-xs text-yellow-600">Top up your Universal Key balance, then click Resume.</p>
            </div>
            <button
              onClick={resumeOrRetry}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-yellow-600 text-white hover:bg-yellow-700 transition-colors shadow-sm"
              data-testid="resume-paused-btn"
            >
              <Play className="w-4 h-4" />
              Resume
            </button>
          </div>
        )}

        {taskStatus === 'failed' && !generating && (
          <div className="flex items-center gap-3 p-3 bg-red-50 border-2 border-red-200 rounded-lg" data-testid="voiceover-error-panel">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700 flex-1">{taskError}</p>
            <button
              onClick={resumeOrRetry}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors shadow-sm"
              data-testid="retry-voiceover-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        )}

        {/* Text modified warning */}
        {isTextModified && (
          <div className="flex items-center gap-3 p-2.5 bg-violet-50 border border-violet-200 rounded-lg" data-testid="voiceover-text-modified-warning">
            <Pencil className="w-4 h-4 text-violet-500 flex-shrink-0" />
            <p className="text-xs text-violet-700 flex-1">Script has been modified. Click <b>Regenerate Voiceover</b> to update the audio.</p>
          </div>
        )}

        {/* Stale voiceover warning — after video was cut */}
        {isStale && !taskStatus && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="voiceover-stale-warning">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Voiceover out of sync</p>
              <p className="text-xs text-amber-600">Video was edited. Regenerate voiceover to re-sync with the new video duration.</p>
            </div>
            <button
              onClick={generateVoiceover}
              disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
              data-testid="regenerate-stale-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Re-sync
            </button>
          </div>
        )}

        {/* Synced Video + Audio Preview Player */}
        {video.voiceover_generated && audioSrc && videoSrc && !generating && (
          <div className="rounded-lg overflow-hidden bg-slate-900" data-testid="synced-preview-player">
            <div className="relative">
              <video
                ref={vidPreviewRef}
                src={videoSrc}
                className="w-full max-h-56 object-contain"
                muted
                playsInline
                preload="metadata"
                onLoadedMetadata={() => {
                  const v = vidPreviewRef.current;
                  if (v?.duration && isFinite(v.duration)) setPreviewDuration(v.duration);
                }}
                onEnded={() => setPreviewPlaying(false)}
                data-testid={`preview-video-${video.id}`}
              />
              {/* Play overlay */}
              {!previewPlaying && (
                <div
                  className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/30 hover:bg-black/40 transition-colors"
                  onClick={togglePreviewPlay}
                >
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 text-slate-800 ml-0.5" />
                  </div>
                </div>
              )}
            </div>
            {/* Audio element (hidden, used for sync) */}
            <audio
              ref={audioPreviewRef}
              src={audioSrc}
              preload="metadata"
              onEnded={() => { setPreviewPlaying(false); if (vidPreviewRef.current) vidPreviewRef.current.pause(); }}
            />
            {/* Custom scrub bar */}
            <div className="px-4 py-2.5 bg-slate-800 flex items-center gap-3">
              <button
                onClick={togglePreviewPlay}
                className="p-1 text-white hover:text-blue-400 transition-colors"
                data-testid={`preview-play-btn-${video.id}`}
              >
                {previewPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <span className="text-xs text-slate-400 font-mono w-10 text-right">{fmtTime(previewTime)}</span>
              <input
                type="range"
                min="0"
                max={previewDuration || 1}
                step="0.1"
                value={previewTime}
                onChange={handlePreviewSeek}
                className="flex-1 h-1.5 bg-slate-600 rounded-full appearance-none cursor-pointer accent-blue-500
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer"
                data-testid={`preview-seek-${video.id}`}
              />
              <span className="text-xs text-slate-400 font-mono w-10">{fmtTime(previewDuration)}</span>
              <span className="text-[9px] text-blue-400 bg-blue-900/40 px-1.5 py-0.5 rounded">Video + AI Voice</span>
            </div>
          </div>
        )}

        {/* Voice selector row */}
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500">Voice:</label>
          <select
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2.5 py-1.5 bg-white focus:ring-1 focus:ring-blue-200"
            data-testid="voice-select"
          >
            {(voices || []).map(v => {
              const val = typeof v === 'string' ? v : v.value;
              const lbl = typeof v === 'string' ? v.charAt(0).toUpperCase() + v.slice(1) : v.label;
              return <option key={val} value={val}>{lbl}</option>;
            })}
          </select>
          <span className="text-[10px] text-slate-400 ml-auto">Continuous narration — one script, one audio track</span>
        </div>

        {/* Single script textarea */}
        <div>
          <label className="text-xs font-medium text-slate-600 mb-1.5 block">Voiceover Script</label>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              if (video.voiceover_generated && e.target.value.trim() !== savedTextRef.current.trim()) {
                setTextModified(true);
              } else {
                setTextModified(false);
              }
            }}
            placeholder="Write or edit the voiceover narration for this video. The full text will be converted to a single continuous audio track."
            className="w-full p-3 border border-slate-200 rounded-lg text-sm leading-relaxed resize-vertical focus:ring-2 focus:ring-blue-100 focus:border-blue-300 placeholder:text-slate-300"
            style={{ minHeight: '140px' }}
            rows={Math.max(6, Math.min(18, Math.ceil(text.length / 90)))}
            data-testid="voiceover-script-textarea"
          />
        </div>

        {/* Generate button + progress + retry */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={generateVoiceover}
            disabled={generating || !text.trim()}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            data-testid="generate-voiceover-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> {video.voiceover_generated ? 'Regenerate Voiceover' : 'Generate Voiceover'}</>
            )}
          </button>
          {genProgress && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded" data-testid="voiceover-progress">{genProgress}</span>
          )}
        </div>

        {/* Standalone audio player (fallback when no video src) */}
        {video.voiceover_audio_path && !generating && !videoSrc && (
          <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg" data-testid="voiceover-audio-player">
            <audio
              controls
              className="w-full h-8"
              src={audioSrc}
              key={video.voiceover_audio_path}
            />
          </div>
        )}

        {/* Video actions: Cut + Download with AI Voice */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={() => onEditVideo?.(video)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
            data-testid={`vo-cut-video-${video.id}`}
          >
            <Scissors className="w-3.5 h-3.5" />
            Cut Video
          </button>
          <button
            onClick={async () => {
              try {
                toast.info('Preparing download...');
                const response = await axios.get(`${API}/projects/${projectId}/videos/${video.id}/download-with-voiceover`, { responseType: 'blob', timeout: 600000 });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${video.filename || 'video'}_with_voiceover.mp4`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Video with AI voice downloaded');
              } catch {
                toast.error('Failed to download — ensure voiceover is generated');
              }
            }}
            disabled={!video.voiceover_generated || isStale}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-green-200 text-green-700 hover:bg-green-50 hover:border-green-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid={`vo-download-with-voice-${video.id}`}
          >
            <Download className="w-3.5 h-3.5" />
            Download with AI Voice
          </button>
          {audioSrc && !generating && (
            <button
              onClick={() => {
                const link = document.createElement('a');
                link.href = audioSrc;
                link.setAttribute('download', `${video.filename || 'audio'}_voiceover.mp3`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Audio downloaded');
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors"
              data-testid={`vo-download-audio-${video.id}`}
            >
              <Music className="w-3.5 h-3.5" />
              Audio Only
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};


// Sortable Step Component - Supports multiple images per step
const SortableStep = ({ step, index, updateStep, deleteStep, clipboardImage, setClipboardImage }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: step.id });
  
  const imageInputRef = useRef(null);
  const addImageInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [replacingImageIndex, setReplacingImageIndex] = useState(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Get screenshots array (handle both old single screenshot and new array format)
  const getScreenshots = () => {
    if (step.screenshots && Array.isArray(step.screenshots)) {
      return step.screenshots;
    }
    if (step.screenshot) {
      return [step.screenshot];
    }
    return [];
  };

  const screenshots = getScreenshots();

  // Add a new image to the screenshots array
  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        const newScreenshots = [...screenshots, base64];
        updateStep(step.id, 'screenshots', newScreenshots);
        // Also update legacy field for backwards compatibility
        if (newScreenshots.length === 1) {
          updateStep(step.id, 'screenshot', base64);
        }
        toast.success('Image added');
        setUploadingImage(false);
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
    
    if (addImageInputRef.current) {
      addImageInputRef.current.value = '';
    }
  };

  // Replace an image at a specific index
  const handleReplaceImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || replacingImageIndex === null) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        const newScreenshots = [...screenshots];
        newScreenshots[replacingImageIndex] = base64;
        updateStep(step.id, 'screenshots', newScreenshots);
        // Update legacy field
        if (replacingImageIndex === 0) {
          updateStep(step.id, 'screenshot', base64);
        }
        toast.success('Image replaced');
        setUploadingImage(false);
        setReplacingImageIndex(null);
      };
      reader.onerror = () => {
        toast.error('Failed to read image');
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
    
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const handleDeleteImage = (imgIndex) => {
    const newScreenshots = screenshots.filter((_, i) => i !== imgIndex);
    updateStep(step.id, 'screenshots', newScreenshots);
    // Update legacy field
    updateStep(step.id, 'screenshot', newScreenshots[0] || null);
    toast.success('Image removed');
  };

  const handleCopyImage = (imgIndex) => {
    setClipboardImage(screenshots[imgIndex]);
    toast.success('Image copied — use Paste on any step');
  };

  const handlePasteImage = () => {
    if (!clipboardImage) return;
    const newScreenshots = [...screenshots, clipboardImage];
    updateStep(step.id, 'screenshots', newScreenshots);
    if (newScreenshots.length === 1) {
      updateStep(step.id, 'screenshot', clipboardImage);
    }
    toast.success('Image pasted');
  };

  const triggerReplaceImage = (imgIndex) => {
    setReplacingImageIndex(imgIndex);
    setTimeout(() => imageInputRef.current?.click(), 0);
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="p-6 border border-slate-200 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex flex-col items-center gap-2 pt-2">
            <div
              {...attributes}
              {...listeners}
              className="cursor-move hover:bg-slate-100 rounded p-1"
            >
              <GripVertical className="w-5 h-5 text-slate-400" />
            </div>
            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
              {index + 1}
            </div>
            {step.timestamp != null && step.timestamp > 0 && (
              <span className="text-[10px] text-slate-400 font-mono ml-1" data-testid={`step-timestamp-${step.id}`}>
                {Math.floor(step.timestamp / 60)}:{String(Math.floor(step.timestamp % 60)).padStart(2, '0')}
              </span>
            )}
          </div>
          
          <div className="flex-1 space-y-4">
            <Input
              data-testid={`step-title-${step.id}`}
              value={step.title}
              onChange={(e) => updateStep(step.id, 'title', e.target.value)}
              placeholder="Step title"
              className="text-lg font-semibold border-slate-300"
            />
            
            <ReactQuill
              theme="snow"
              value={step.description}
              onChange={(value) => updateStep(step.id, 'description', value)}
              placeholder="Step description..."
              className="bg-white"
            />
            
            {/* Multiple Images Section */}
            <div className="mt-4 space-y-3">
              {/* Existing Images Grid */}
              {screenshots.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {screenshots.map((img, imgIndex) => (
                    <div key={imgIndex} className="relative group">
                      <img 
                        src={img} 
                        alt={`Screenshot ${imgIndex + 1} for ${step.title}`}
                        className="rounded-md border border-slate-200 w-full object-contain max-h-64"
                      />
                      {/* Image number badge */}
                      <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center">
                        {imgIndex + 1}
                      </div>
                      {/* Image Edit Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex gap-1.5">
                          <Button
                            data-testid={`replace-image-${step.id}-${imgIndex}`}
                            size="sm"
                            variant="secondary"
                            onClick={() => triggerReplaceImage(imgIndex)}
                            disabled={uploadingImage}
                            className="bg-white hover:bg-slate-100 text-xs px-2 py-1 h-7"
                          >
                            <Upload className="w-3 h-3 mr-1" />
                            Replace
                          </Button>
                          <Button
                            data-testid={`copy-image-${step.id}-${imgIndex}`}
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCopyImage(imgIndex)}
                            className="bg-white hover:bg-slate-100 text-xs px-2 py-1 h-7"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Button
                            data-testid={`delete-image-${step.id}-${imgIndex}`}
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteImage(imgIndex)}
                            className="text-xs px-2 py-1 h-7"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add Image Placeholder / Button */}
              <div className="flex gap-2">
                <div 
                  className="flex-1 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-slate-50 transition-colors"
                  onClick={() => addImageInputRef.current?.click()}
                >
                  <Plus className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">
                    {screenshots.length === 0 ? 'Click to add screenshot' : 'Add another image'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG up to 10MB</p>
                </div>
                {clipboardImage && (
                  <div
                    data-testid={`paste-image-${step.id}`}
                    className="border-2 border-dashed border-blue-400 rounded-lg p-6 text-center cursor-pointer hover:border-blue-600 hover:bg-blue-50 transition-colors min-w-[140px]"
                    onClick={handlePasteImage}
                  >
                    <Clipboard className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm text-blue-600 font-medium">Paste Image</p>
                  </div>
                )}
              </div>
              
              {/* Hidden file inputs */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleReplaceImage}
                className="hidden"
              />
              <input
                ref={addImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleAddImage}
                className="hidden"
              />
            </div>
          </div>
          
          <Button
            data-testid={`delete-step-${step.id}`}
            variant="ghost"
            size="sm"
            onClick={() => deleteStep(step.id)}
            className="hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Video player with custom scrub bar matching the AI voice preview style
const VideoPlayerWithError = ({ video, projectId }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [playerDuration, setPlayerDuration] = useState(video.duration || 0);
  const [playerTime, setPlayerTime] = useState(0);
  const [isVidPlaying, setIsVidPlaying] = useState(false);
  const vidRef = useRef(null);
  const retryCount = useRef(0);
  // Cache-bust: append video path hash + timestamp to prevent stale caching
  const cacheBuster = `v=${encodeURIComponent(video.path || '')}&t=${video.duration || 0}`;
  const src = `${API}/uploads/${encodeURIComponent(video.path?.split('/').pop() || video.filename)}?${cacheBuster}`;

  // Sync duration when video prop changes (e.g., after cut)
  useEffect(() => {
    if (video.duration > 0) setPlayerDuration(video.duration);
  }, [video.duration]);

  // Reset state when video path changes (new edit produced)
  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
    retryCount.current = 0;
    setPlayerTime(0);
  }, [video.path]);

  const onVideoError = useCallback(() => {
    if (retryCount.current < 3) {
      retryCount.current++;
      setTimeout(() => {
        const v = vidRef.current;
        if (v) v.load();
      }, 1000 * retryCount.current);
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, []);

  const fmtTime = (s) => {
    if (!s || !isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const onMeta = () => {
    const v = vidRef.current;
    if (v && v.duration && isFinite(v.duration) && v.duration > 0 && v.duration < 86400) {
      setPlayerDuration(v.duration);
    }
    setIsLoading(false);
  };

  const onCanPlay = () => setIsLoading(false);

  // Fetch duration from backend if not available from DB or browser
  useEffect(() => {
    if (playerDuration > 0 || !video.id || !projectId) return;
    const fetchDur = async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/videos/${video.id}/duration`);
        if (res.data.duration > 0) setPlayerDuration(res.data.duration);
      } catch {}
    };
    const t = setTimeout(fetchDur, 1500);
    return () => clearTimeout(t);
  }, [video.id, projectId, playerDuration]);

  const toggleVidPlay = () => {
    const v = vidRef.current;
    if (!v) return;
    if (isVidPlaying) { v.pause(); } else { v.play().catch(() => {}); }
  };

  const handleVidSeek = (e) => {
    const t = parseFloat(e.target.value);
    if (vidRef.current) {
      vidRef.current.currentTime = t;
      setPlayerTime(t);
    }
  };

  const dur = playerDuration > 0 ? playerDuration : (video.duration || 0);

  return (
    <div className="rounded-lg overflow-hidden bg-slate-900">
      <div className="relative">
        {/* Loading overlay */}
        {isLoading && !hasError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900/80">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
              <p className="text-xs text-slate-400">Loading video...</p>
            </div>
          </div>
        )}
        {/* Edited badge */}
        {video.edited && (
          <div className="absolute top-2 left-2 z-10 px-2 py-0.5 bg-blue-600/90 text-white text-[10px] font-medium rounded-full">
            Edited
          </div>
        )}
        <video
          ref={vidRef}
          src={src}
          className={`w-full max-h-80 object-contain cursor-pointer ${hasError ? 'hidden' : ''}`}
          playsInline
          preload="metadata"
          onLoadedMetadata={onMeta}
          onDurationChange={onMeta}
          onCanPlay={onCanPlay}
          onTimeUpdate={() => vidRef.current && setPlayerTime(vidRef.current.currentTime)}
          onPlay={() => setIsVidPlaying(true)}
          onPause={() => setIsVidPlaying(false)}
          onEnded={() => setIsVidPlaying(false)}
          onError={onVideoError}
          onClick={toggleVidPlay}
          data-testid={`project-video-${video.id}`}
        />
        {hasError && (
          <div className="flex items-center justify-center h-48 text-white/70 text-center p-4">
            <div>
              <Video className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Video is loading or temporarily unavailable</p>
              <button 
                onClick={() => { setHasError(false); setIsLoading(true); retryCount.current = 0; if (vidRef.current) vidRef.current.load(); }}
                className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                data-testid={`video-retry-${video.id}`}
              >
                Retry
              </button>
            </div>
          </div>
        )}
        {/* Play/Pause overlay */}
        {!hasError && !isLoading && (
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
            onClick={toggleVidPlay}
          >
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
              {isVidPlaying ? (
                <Pause className="w-7 h-7 text-slate-800" />
              ) : (
                <Play className="w-7 h-7 text-slate-800 ml-1" />
              )}
            </div>
          </div>
        )}
      </div>
      {/* Custom scrub bar — matching the AI voice preview style */}
      <div className="px-4 py-3 bg-slate-800 flex items-center gap-3" data-testid={`video-scrubbar-${video.id}`}>
        <button 
          onClick={toggleVidPlay} 
          className="p-1 text-white hover:text-blue-400 transition-colors"
          disabled={hasError}
          data-testid={`video-play-btn-${video.id}`}
        >
          {isVidPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <span className="text-xs text-slate-400 font-mono w-10 text-right">{fmtTime(playerTime)}</span>
        <input
          type="range"
          min="0"
          max={dur || 1}
          step="0.1"
          value={playerTime}
          onChange={handleVidSeek}
          className="flex-1 h-1.5 bg-slate-600 rounded-full appearance-none cursor-pointer accent-blue-500
            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 
            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:shadow-md"
          disabled={hasError || dur === 0}
          data-testid={`video-seek-${video.id}`}
        />
        <span className="text-xs text-slate-400 font-mono w-10" data-testid={`video-duration-${video.id}`}>{fmtTime(dur)}</span>
      </div>
      <div className="bg-slate-800 px-4 pb-2">
        <p className="text-white/70 text-xs">{video.filename}</p>
      </div>
    </div>
  );
};

const Editor = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState('');
  const [steps, setSteps] = useState([]);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [activeTab, setActiveTab] = useState('editor');
  const [voiceText, setVoiceText] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  const [generatingVoice, setGeneratingVoice] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showAvatar, setShowAvatar] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [downloadingVideo, setDownloadingVideo] = useState(false);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [clipboardImage, setClipboardImage] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Open share modal if ?share=true in URL
  useEffect(() => {
    if (searchParams.get('share') === 'true') {
      setShowShareModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    
    if (audio) {
      const handleAudioEnded = () => {
        setIsPlaying(false);
        if (video) video.pause();
      };
      
      audio.addEventListener('ended', handleAudioEnded);
      
      return () => {
        audio.removeEventListener('ended', handleAudioEnded);
      };
    }
  }, [audioUrl]);

  // Sync video with audio
  useEffect(() => {
    const audio = audioRef.current;
    const video = videoRef.current;
    
    if (audio && video) {
      const syncVideoToAudio = () => {
        const timeDiff = Math.abs(video.currentTime - audio.currentTime);
        // If difference is more than 0.3 seconds, sync them
        if (timeDiff > 0.3) {
          video.currentTime = audio.currentTime;
        }
      };
      
      // Sync every 500ms while playing
      const syncInterval = setInterval(() => {
        if (isPlaying) {
          syncVideoToAudio();
        }
      }, 500);
      
      return () => clearInterval(syncInterval);
    }
  }, [isPlaying, audioUrl]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      setSteps(response.data.steps || []);
      setDocumentTitle(response.data.title || '');
      setDocumentDescription(response.data.description || '');
      setUploadedVideos(response.data.videos || []);
      if (response.data.voiceover_text) {
        setVoiceText(response.data.voiceover_text);
      } else if (response.data.steps && response.data.steps.length > 0) {
        // Fallback: build voiceover text from step descriptions
        const fallback = response.data.steps
          .map(s => (s.description || '').replace(/<[^>]*>/g, ' ').trim())
          .filter(Boolean)
          .join(' ');
        if (fallback) setVoiceText(fallback);
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        toast.error(`${file.name} is not a video file`);
        continue;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      // Retry upload up to 3 times for large files
      let success = false;
      for (let attempt = 1; attempt <= 3 && !success; attempt++) {
        try {
          if (attempt > 1) toast.info(`Retrying upload (attempt ${attempt}/3)...`);
          const response = await axios.post(`${API}/projects/${projectId}/upload-video`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000, // 5-minute timeout for large files
          });
          toast.success(`${file.name} uploaded successfully`);
          setUploadedVideos(response.data.videos || []);
          fetchProject();
          success = true;
        } catch (error) {
          if (attempt === 3) {
            const msg = error.response?.data?.detail || error.message || 'Upload failed';
            toast.error(`Failed to upload ${file.name}: ${msg}`);
          }
        }
      }
      setUploading(false);
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      const response = await axios.delete(`${API}/projects/${projectId}/videos/${videoId}`);
      setUploadedVideos(response.data.videos || []);
      toast.success('Video removed');
      fetchProject();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const analyzeVideo = async () => {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      const startRes = await axios.post(`${API}/projects/${projectId}/analyze-video`);
      const taskId = startRes.data.task_id;
      
      if (!taskId) {
        // Fallback for old sync response
        if (startRes.data.steps) {
          setSteps(startRes.data.steps);
          if (startRes.data.title) setDocumentTitle(startRes.data.title);
          if (startRes.data.description) setDocumentDescription(startRes.data.description);
          if (startRes.data.voiceover_text) setVoiceText(startRes.data.voiceover_text);
          fetchProject();
        }
        toast.success('Video analysed successfully');
        setAnalyzing(false);
        return;
      }

      toast.info('AI is analysing your video... This may take a few minutes.');
      
      // Poll with exponential backoff: 3s → 5s → 8s → 10s (cap)
      let pollCount = 0;
      const maxPolls = 360; // ~30 min at avg 5s intervals
      let interval = 3000;
      while (pollCount < maxPolls) {
        await new Promise(r => setTimeout(r, interval));
        // Exponential backoff: 3s, 5s, 8s, 10s cap
        interval = Math.min(10000, Math.floor(interval * 1.3));
        pollCount++;
        try {
          const res = await axios.get(`${API}/analysis-tasks/${taskId}`, { timeout: 15000 });
          const { status, error, result, progress } = res.data;
          
          // Show live progress to user
          if (progress && status === 'processing') {
            setAnalysisProgress(progress);
            toast.info(progress, { id: 'analysis-progress', duration: 5000 });
            // Reset backoff when we see progress (task is actively running)
            interval = 3000;
          }
          
          if (status === 'completed' && result) {
            toast.dismiss('analysis-progress');
            toast.success('Video analysed successfully!');
            setAnalysisProgress('');
            setSteps(result.steps || []);
            if (result.title) setDocumentTitle(result.title);
            if (result.description) setDocumentDescription(result.description);
            if (result.voiceover_text) setVoiceText(result.voiceover_text);
            const videosWithVoiceover = (result.videos || []).filter(v => v.voiceover_generated);
            if (videosWithVoiceover.length > 0) {
              toast.success(`Voice-overs auto-generated for ${videosWithVoiceover.length} video(s)`, { duration: 4000 });
            }
            fetchProject();
            setAnalyzing(false);
            return;
          }
          if (status === 'failed') {
            toast.dismiss('analysis-progress');
            const errMsg = error || 'Video analysis failed. You can try again.';
            toast.error(errMsg, { duration: 8000 });
            setAnalysisError(errMsg);
            setAnalysisProgress('');
            setAnalyzing(false);
            return;
          }
          // 'interrupted' = auto-resuming, keep polling
          if (status === 'interrupted') {
            setAnalysisProgress('Auto-resuming after restart...');
          }
        } catch {
          // Network error — keep trying with backoff
        }
      }
      toast.dismiss('analysis-progress');
      toast.error('Video analysis timed out. Please try again.');
    } catch (error) {
      const errMsg = error.response?.data?.detail || 'Failed to analyse video';
      toast.error(errMsg);
      setAnalysisError(errMsg);
    }
    setAnalyzing(false);
  };

  const saveSteps = async () => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { 
        steps,
        title: documentTitle,
        description: documentDescription
      });
      toast.success('Changes saved');
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  const addStep = () => {
    const newStep = {
      id: Date.now().toString(),
      title: 'New Step',
      description: 'Add step description here',
      screenshot: null,
      order: steps.length
    };
    setSteps([...steps, newStep]);
  };

  const updateStep = useCallback((stepId, field, value) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId ? { ...step, [field]: value } : step
      )
    );
  }, []);

  const deleteStep = (stepId) => {
    setSteps(steps.filter(step => step.id !== stepId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const generateVoiceover = async () => {
    if (!voiceText.trim()) {
      toast.error('Please enter text for voice-over');
      return;
    }

    setGeneratingVoice(true);
    try {
      const response = await axios.post(`${API}/voiceover`, {
        text: voiceText,
        voice: selectedVoice,
        speed: 1.0
      });
      const audioBase64 = response.data.audio_base64;
      const url = `data:audio/mp3;base64,${audioBase64}`;
      setAudioUrl(url);
      toast.success('Voice-over generated successfully');
    } catch (error) {
      toast.error('Failed to generate voice-over');
    } finally {
      setGeneratingVoice(false);
    }
  };

  const togglePlayPause = async () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      if (videoRef.current) videoRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        const audio = audioRef.current;
        const video = videoRef.current;
        
        // Reset to start if at the end
        if (audio.currentTime >= audio.duration - 0.5) {
          audio.currentTime = 0;
          if (video) video.currentTime = 0;
        }
        
        // Start video first, then audio (with tiny delay for sync)
        if (video) {
          video.muted = true; // Mute original video audio
          video.currentTime = audio.currentTime; // Sync positions
          await video.play();
          
          // Small delay to let video buffer
          setTimeout(async () => {
            await audio.play();
            setIsPlaying(true);
          }, 100);
        } else {
          await audio.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Playback error:', error);
        toast.error('Failed to play media');
      }
    }
  };

  const exportDocument = async (format) => {
    setExporting(true);
    try {
      await saveSteps();
      const response = await axios.get(`${API}/projects/${projectId}/export/${format}`, {
        responseType: 'blob',
        timeout: 300000 // 5 minutes for large document generation
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project.name}.${format === 'pdf' ? 'pdf' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Failed to export as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  const downloadAudio = async () => {
    setDownloadingAudio(true);
    try {
      // First generate if not already generated
      if (!audioUrl) {
        const response = await axios.post(`${API}/projects/${projectId}/generate-voiceover`, {
          voice: selectedVoice,
          speed: 1.0
        });
        const url = `data:audio/mp3;base64,${response.data.audio_base64}`;
        setAudioUrl(url);
      }
      
      const response = await axios.get(`${API}/projects/${projectId}/download/audio`, {
        responseType: 'blob',
        timeout: 300000 // 5 minutes
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentTitle || project?.name || 'voiceover'}_audio.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Audio downloaded');
    } catch (error) {
      // If download fails, try to download from local audioUrl
      if (audioUrl) {
        const link = document.createElement('a');
        link.href = audioUrl;
        link.setAttribute('download', `${documentTitle || project?.name || 'voiceover'}_audio.mp3`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Audio downloaded');
      } else {
        toast.error('No audio available. Generate voiceover first.');
      }
    } finally {
      setDownloadingAudio(false);
    }
  };

  const downloadVideo = async () => {
    if (!project?.video_path && uploadedVideos.length === 0) {
      toast.error('No video available');
      return;
    }
    
    setDownloadingVideo(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/download/video`, {
        responseType: 'blob',
        timeout: 600000 // 10 minutes for large videos
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentTitle || project?.name || 'video'}_video.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Video downloaded');
    } catch (error) {
      toast.error('Failed to download video');
    } finally {
      setDownloadingVideo(false);
    }
  };

  const downloadAllVideosWithVoiceover = async () => {
    if (uploadedVideos.length === 0 && !project?.video_path) {
      toast.error('No videos available');
      return;
    }
    
    setDownloadingVideo(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/download/all-videos-with-voiceover`, {
        responseType: 'blob',
        timeout: 900000 // 15 minutes for multiple videos with voiceover merge
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = uploadedVideos.length > 1 ? '.zip' : '.mp4';
      link.setAttribute('download', `${documentTitle || project?.name || 'videos'}_with_voiceover${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(uploadedVideos.length > 1 ? 'All videos downloaded as ZIP' : 'Video with AI voice-over downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download. Ensure videos are analysed first.');
    } finally {
      setDownloadingVideo(false);
    }
  };

  const voices = [
    { value: 'alloy', label: 'Alloy (Neutral)' },
    { value: 'echo', label: 'Echo (Calm)' },
    { value: 'fable', label: 'Fable (Expressive)' },
    { value: 'nova', label: 'Nova (Energetic)' },
    { value: 'onyx', label: 'Onyx (Deep)' },
    { value: 'shimmer', label: 'Shimmer (Bright)' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="hover:bg-slate-100 text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <img src={LOGO_URL} alt="FlowSnap" className="h-14" />
            <div className="h-8 w-px bg-slate-200"></div>
            <h1 className="text-xl font-bold text-slate-800 truncate max-w-xs">{project?.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="share-btn"
              onClick={() => setShowShareModal(true)}
              variant="outline"
              className="border-slate-300 hover:bg-slate-50 hover:border-primary"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button
              data-testid="save-btn"
              onClick={saveSteps}
              variant="outline"
              className="border-slate-300 hover:bg-green-50 hover:border-green-500 hover:text-green-600"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
            
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-primary text-white hover:bg-blue-700 shadow-md hover:shadow-lg transition-all">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => exportDocument('pdf')}
                  disabled={exporting || steps.length === 0}
                  className="cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2 text-red-500" />
                  Export as PDF
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => exportDocument('word')}
                  disabled={exporting || steps.length === 0}
                  className="cursor-pointer"
                >
                  <FileText className="w-4 h-4 mr-2 text-blue-500" />
                  Export as Word
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={downloadVideo}
                  disabled={downloadingVideo || (uploadedVideos.length === 0 && !project?.video_path)}
                  className="cursor-pointer"
                >
                  <Video className="w-4 h-4 mr-2 text-purple-500" />
                  Download Original Video
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={downloadAllVideosWithVoiceover}
                  disabled={downloadingVideo || (uploadedVideos.length === 0 && !project?.video_path)}
                  className="cursor-pointer"
                >
                  <Video className="w-4 h-4 mr-2 text-green-500" />
                  {uploadedVideos.length > 1 ? 'Download All Videos + AI Voice (ZIP)' : 'Download Video + AI Voice'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={downloadAudio}
                  disabled={downloadingAudio || !voiceText}
                  className="cursor-pointer"
                >
                  <Music className="w-4 h-4 mr-2 text-green-500" />
                  Download Audio
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="editor" data-testid="editor-tab">
              <FileText className="w-4 h-4 mr-2" />
              Editor
            </TabsTrigger>
            <TabsTrigger value="voiceover" data-testid="voiceover-tab">
              <Mic className="w-4 h-4 mr-2" />
              Voice-over
            </TabsTrigger>
          </TabsList>

          {/* Editor Tab */}
          <TabsContent value="editor" className="space-y-6">
            {/* Video Section - Show player if analyzed, upload if not */}
            <Card className="p-6 border border-slate-200">
              <div className="space-y-4">
                {/* If project has steps (already analyzed), show video player */}
                {steps.length > 0 && uploadedVideos.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Project Videos</h3>
                        <p className="text-sm text-slate-500">{uploadedVideos.length} video(s)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="video/*"
                          multiple
                          onChange={handleVideoUpload}
                          className="hidden"
                        />
                        <Button
                          data-testid="add-more-video-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          variant="outline"
                          size="sm"
                          className="border-slate-300 text-slate-600 hover:bg-slate-50"
                        >
                          {uploading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Add Video
                            </>
                          )}
                        </Button>
                        <Button
                          data-testid="reanalyze-video-btn"
                          onClick={() => analyzeVideo()}
                          disabled={analyzing}
                          variant="outline"
                          size="sm"
                          className="border-amber-300 text-amber-600 hover:bg-amber-50"
                        >
                          {analyzing ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 mr-2"></div>
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Re-analyze
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {/* Video Player */}
                    <div className="grid gap-4">
                      {uploadedVideos.slice(0, 2).map((video, idx) => (
                        <div key={video.id} className="space-y-2">
                          <VideoPlayerWithError video={video} projectId={project.id} />
                          <div className="flex items-center justify-between px-1">
                            <span className="text-xs text-slate-500">{video.filename}</span>
                            <div className="flex items-center gap-2">
                              <Button
                                data-testid={`cut-video-${video.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => { setEditingVideo(video); setShowVideoEditor(true); }}
                                className="text-slate-600 hover:text-primary hover:bg-primary/10 h-7 text-xs"
                              >
                                <Scissors className="w-3.5 h-3.5 mr-1" />
                                Cut
                              </Button>
                              <Button
                                data-testid={`download-video-vo-${video.id}`}
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const response = await axios.get(`${API}/projects/${projectId}/videos/${video.id}/download-with-voiceover`, { responseType: 'blob', timeout: 600000 });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `${video.filename || 'video'}_with_voiceover.mp4`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    toast.success('Video with voiceover downloaded');
                                  } catch {
                                    toast.error('Failed to download — generate voiceover first');
                                  }
                                }}
                                disabled={!video.voiceover_generated}
                                className="text-slate-600 hover:text-green-600 hover:bg-green-50 h-7 text-xs"
                              >
                                <Download className="w-3.5 h-3.5 mr-1" />
                                With AI Voice
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {uploadedVideos.length > 2 && (
                        <p className="text-sm text-slate-500 text-center">
                          +{uploadedVideos.length - 2} more video(s) - View in Voice-over tab
                        </p>
                      )}
                    </div>
                    {/* Re-analyze hint */}
                    <p className="text-xs text-slate-400 text-center">
                      Not happy with the generated steps? Click "Re-analyze" to regenerate, or add more videos.
                    </p>
                  </>
                ) : (
                  /* Upload section for new projects */
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Videos</h3>
                        <p className="text-sm text-slate-500">Upload one or more videos to generate documentation</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleVideoUpload}
                        className="hidden"
                      />
                      <Button
                        data-testid="upload-video-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        variant="outline"
                        className="border-primary text-primary hover:bg-blue-50"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Video
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Uploaded Videos List */}
                    {uploadedVideos.length > 0 ? (
                      <div className="space-y-2">
                        {uploadedVideos.map((video, idx) => (
                          <div 
                            key={video.id} 
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Video className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{video.filename}</p>
                                <p className="text-xs text-slate-500">Video {idx + 1}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                data-testid={`edit-video-${video.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingVideo(video);
                                  setShowVideoEditor(true);
                                }}
                                className="text-slate-600 hover:text-primary hover:bg-primary/10"
                              >
                                <Scissors className="w-4 h-4 mr-1" />
                                Cut
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteVideo(video.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                        <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No videos uploaded yet</p>
                        <p className="text-sm text-slate-400">Click "Add Video" to upload</p>
                      </div>
                    )}
                  </>
                )}

                {/* Analyze Button */}
                {uploadedVideos.length > 0 && steps.length === 0 && (
                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-medium text-slate-900">Ready to analyze</p>
                        <p className="text-sm text-slate-500">
                          {uploadedVideos.length} video{uploadedVideos.length > 1 ? 's' : ''} will be processed into a combined document
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid="analyze-video-btn"
                      onClick={analyzeVideo}
                      disabled={analyzing}
                      className="w-full bg-gradient-to-r from-primary to-accent text-white hover:opacity-90"
                      size="lg"
                    >
                      {analyzing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Analysing Videos...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5 mr-2" />
                          Analyse {uploadedVideos.length > 1 ? 'All Videos' : 'Video'} with AI
                        </>
                      )}
                    </Button>
                    {analyzing && analysisProgress && (
                      <div className="flex items-center gap-2 py-2 px-3 bg-primary/5 rounded-lg text-sm text-primary" data-testid="analysis-progress">
                        <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                        <span className="truncate">{analysisProgress}</span>
                      </div>
                    )}
                    {analysisError && !analyzing && (
                      <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mt-2" data-testid="analysis-error-panel">
                        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-xs text-red-700 flex-1">{analysisError}</p>
                        <Button
                          onClick={() => { setAnalysisError(null); analyzeVideo(); }}
                          size="sm"
                          className="bg-red-600 text-white hover:bg-red-700"
                          data-testid="retry-analysis-btn"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Retry
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Steps Editor */}
            <div className="space-y-6">
              {/* Document Title Section - Kommodo Style */}
              {steps.length > 0 && (
                <div className="text-center py-8 border-b border-slate-200">
                  {editingTitle ? (
                    <div className="max-w-2xl mx-auto space-y-4">
                      <Input
                        data-testid="document-title-input"
                        value={documentTitle}
                        onChange={(e) => setDocumentTitle(e.target.value)}
                        placeholder="Document Title"
                        className="text-2xl font-bold text-center border-slate-300"
                        autoFocus
                      />
                      <textarea
                        data-testid="document-description-input"
                        value={documentDescription}
                        onChange={(e) => setDocumentDescription(e.target.value)}
                        placeholder="Add a description of what this guide covers..."
                        className="w-full text-center text-slate-600 border border-slate-300 rounded-md p-3 resize-none"
                        rows={2}
                      />
                      <div className="flex items-center justify-center gap-3 pt-2">
                        <Button
                          data-testid="hero-save-btn"
                          onClick={() => { setEditingTitle(false); saveSteps(); }}
                          className="bg-primary text-white hover:bg-blue-700"
                          size="sm"
                        >
                          <Save className="w-4 h-4 mr-1.5" />
                          Save
                        </Button>
                        <Button
                          data-testid="hero-cancel-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDocumentTitle(project?.title || '');
                            setDocumentDescription(project?.description || '');
                            setEditingTitle(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          data-testid="hero-clear-btn"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setDocumentTitle(''); setDocumentDescription(''); }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1.5" />
                          Clear All
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="py-4 rounded-lg relative"
                      data-testid="editable-hero-section"
                    >
                      <h1 className="text-3xl font-bold text-slate-900 mb-2">
                        {documentTitle || <span className="text-slate-400 italic">No title set</span>}
                      </h1>
                      <p className="text-lg text-slate-600 mb-4">
                        {documentDescription || <span className="text-slate-400 italic">No description set</span>}
                      </p>
                      <div className="flex items-center justify-center gap-3">
                        <Button
                          data-testid="hero-edit-btn"
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingTitle(true)}
                          className="border-slate-300 hover:border-primary hover:bg-blue-50"
                        >
                          <Pencil className="w-4 h-4 mr-1.5" />
                          Edit Title & Description
                        </Button>
                        {(documentTitle || documentDescription) && (
                          <Button
                            data-testid="hero-delete-btn"
                            variant="ghost"
                            size="sm"
                            onClick={() => { setDocumentTitle(''); setDocumentDescription(''); saveSteps(); }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1.5" />
                            Delete
                          </Button>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mt-4">
                        {steps.length} step{steps.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">Steps</h2>
                <Button
                  data-testid="add-step-btn"
                  onClick={addStep}
                  variant="outline"
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Step
                </Button>
              </div>

              {steps.length === 0 && (
                <Card className="p-12 border-2 border-dashed border-slate-300">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No steps yet</h3>
                    <p className="text-slate-600 mb-4">Add your first step manually or upload a video to analyse</p>
                  </div>
                </Card>
              )}

              {steps.length > 0 && (
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={steps.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {steps.map((step, index) => (
                        <SortableStep
                          key={step.id}
                          step={step}
                          index={index}
                          updateStep={updateStep}
                          deleteStep={deleteStep}
                          clipboardImage={clipboardImage}
                          setClipboardImage={setClipboardImage}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </TabsContent>

          {/* Voice-over Tab */}
          <TabsContent value="voiceover">
            <Card className="p-8 border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">AI Voice-over</h2>
              <p className="text-slate-600 mb-6">Generate AI voice-over for each video separately</p>
              
              {uploadedVideos.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-lg">
                  <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No videos uploaded yet</p>
                  <p className="text-sm text-slate-400">Upload videos in the Editor tab first</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {uploadedVideos.map((video, idx) => (
                    <VideoVoiceoverCard 
                      key={video.id} 
                      video={video} 
                      index={idx}
                      projectId={projectId}
                      voices={voices}
                      projectVoiceText={voiceText}
                      projectSteps={steps}
                      onUpdate={fetchProject}
                      onEditVideo={(v) => {
                        setEditingVideo(v);
                        setShowVideoEditor(true);
                      }}
                    />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        project={project}
        onUpdate={fetchProject}
      />

      {/* Video Editor Modal */}
      <VideoEditorModal
        isOpen={showVideoEditor}
        onClose={() => {
          setShowVideoEditor(false);
          setEditingVideo(null);
        }}
        video={editingVideo}
        projectId={projectId}
        onUpdate={fetchProject}
      />
    </div>
  );
};

export default Editor;